import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, parseRateLimitInfo } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, RefreshCw } from "lucide-react";

type InsightsResponse = {
  content: string;
  generatedAt: string;
  cached?: boolean;
  stale?: boolean;
};

function fmtGeneratedAt(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Gemini-generated, plain-English coaching narrative. Defaults to whole-portfolio
 * scope; pass `scope="property"` + `propertyId` to render the same panel scoped
 * to a single property. Each scope is cached independently (24h TTL) with its own
 * Refresh button that forces regeneration.
 */
export function AiCoachingInsightsPanel({
  scope = "portfolio",
  propertyId,
}: {
  scope?: "portfolio" | "property";
  propertyId?: number;
} = {}) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const isProperty = scope === "property" && propertyId != null;
  // Distinct cache key per property so React Query doesn't share the portfolio
  // narrative with property panels (and vice versa).
  const queryKey = isProperty
    ? (["/api/app/analytics/insights", "property", propertyId] as const)
    : (["/api/app/analytics/insights"] as const);
  const requestBody = isProperty ? { scope: "property", scopeId: propertyId } : {};

  const { data, isLoading, error } = useQuery<InsightsResponse>({
    queryKey,
    queryFn: async () => {
      const res = await apiRequest("POST", "/api/app/analytics/insights", requestBody);
      return res.json();
    },
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
  });

  const refreshMut = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/analytics/insights", {
        ...requestBody,
        refresh: true,
      });
      return (await res.json()) as InsightsResponse;
    },
    onSuccess: (fresh) => {
      qc.setQueryData(queryKey, fresh);
      toast({ title: "Insights refreshed" });
    },
    onError: (err) => {
      const rateLimit = parseRateLimitInfo(err);
      toast({
        title: rateLimit ? "Slow down a moment" : "Couldn't refresh insights",
        description: rateLimit
          ? rateLimit.message
          : parseErrorMessage(err, "Please try again in a moment."),
        variant: "destructive",
      });
    },
  });

  return (
    <Card data-testid="ai-insights-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-muted">
              <Sparkles className="h-4 w-4 text-brand" />
            </div>
            <div>
              <CardTitle className="text-base">AI Insights</CardTitle>
              <CardDescription>
                {data?.generatedAt
                  ? `Generated ${fmtGeneratedAt(data.generatedAt)}${data.stale ? " · showing last result" : ""}`
                  : isProperty
                    ? "Plain-English property coaching"
                    : "Plain-English portfolio coaching"}
              </CardDescription>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 shrink-0"
            onClick={() => refreshMut.mutate()}
            disabled={refreshMut.isPending || isLoading}
            data-testid="button-refresh-insights"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshMut.isPending ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading || refreshMut.isPending ? (
          <div className="space-y-2" data-testid="insights-loading">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-10/12" />
            <Skeleton className="h-4 w-9/12" />
          </div>
        ) : error || !data?.content ? (
          <p className="text-sm text-muted-foreground" data-testid="insights-empty">
            AI insights aren&apos;t available right now. Try the Refresh button in a moment.
          </p>
        ) : (
          <div
            className="text-sm leading-relaxed text-foreground whitespace-pre-wrap"
            data-testid="insights-content"
          >
            {data.content}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
